/**
 * The field chrome of Claude Design's mobile document form, as one place.
 *
 * Every input of that screen and its drawers is drawn the same way: a 1px box on white,
 * 8px radius, `--shadow-xs`, 12px of horizontal padding.
 *
 * Sizes are pinned rather than padding-derived because the design pins them. The borders
 * are split out of the base because `border-zinc-300` and `border-branding-red` are plain
 * `border-color` utilities of equal specificity: which one won would be decided by
 * Tailwind's output order rather than by the order they appear in a class list, so only
 * ever one of them is in the string.
 *
 * The design's 14.5px text is deliberately not enforced here. `assets/css/app.scss` clamps
 * every mobile input to 16px so iOS does not zoom the page on focus, and that rule outranks
 * a utility class; opting out would buy 1.5px and cost the zoom.
 */

const BASE = 'w-full rounded-lg border bg-white px-3 text-zinc-900 shadow-ds-xs focus:outline-none';

/** Full-size field, 44px — the main level of a drawer. */
const FIELD = `h-11 ${BASE} text-[14.5px]`;
/** 42px — inside a secondary panel, where the fields are not the main event. */
const PANEL_FIELD = `h-[42px] ${BASE} text-sm`;

const NEUTRAL_BORDER = 'border-zinc-300 focus:border-zinc-400';
const ERROR_BORDER = 'border-branding-red focus:border-branding-red';

export interface DesignSystemFieldOptions {
    /** `panel` for the shorter field used inside a drawer's secondary panel. */
    size?: 'default' | 'panel';
    /** Paints the error border. Callers already know their own validity rules. */
    invalid?: boolean;
}

export default function useDesignSystemField() {
    const fieldClass = ({ size = 'default', invalid = false }: DesignSystemFieldOptions = {}) => `${size === 'panel' ? PANEL_FIELD : FIELD} ${invalid ? ERROR_BORDER : NEUTRAL_BORDER}`;

    /**
     * The same box — border, radius, shadow — for a surface that is not a single-line
     * field and therefore has no business carrying `h-11` or horizontal padding. The
     * rich text editor is the only one: it draws its own frame around the Quill area.
     */
    const surfaceClass = () => 'rounded-lg border border-zinc-300 bg-white shadow-ds-xs';

    return { fieldClass, surfaceClass };
}
